import { pingApi } from './pingService';
import { nuclearReset } from './appResetService';
import { logDebug, logError } from './loggerService';

const FORCE_UPDATE_FLAG = 'hp_force_update_attempted';
const UPDATE_REQUIRED_ROUTE = '/update-required';

/**
 * Compare two version strings of the form `MAJOR.MINOR.PATCH` (or any
 * dot-separated numeric segments). Returns:
 *   negative if `a < b`
 *   0       if `a === b`
 *   positive if `a > b`
 *   null    if either side is unparseable (caller should skip the check)
 *
 * Tolerant of trailing zeros / mismatched segment counts: `1.2` is
 * treated as `1.2.0`.
 */
export function compareSemver(a: string, b: string): number | null {
  const parse = (v: string): number[] | null => {
    if (!v || typeof v !== 'string') return null;
    const parts = v.split('.').map((s) => Number.parseInt(s, 10));
    if (parts.some((n) => Number.isNaN(n))) return null;
    return parts;
  };

  const aParts = parse(a);
  const bParts = parse(b);
  if (!aParts || !bParts) return null;

  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i++) {
    const av = aParts[i] ?? 0;
    const bv = bParts[i] ?? 0;
    if (av !== bv) return av - bv;
  }
  return 0;
}

/**
 * Once-per-boot version check:
 *   1. Fire GET /ping.
 *   2. If response.appVersionRequired is present and parseable AND our
 *      VITE_VERSION is strictly lower, this FE is below the BE's minimum
 *      support cutoff and must update before continuing.
 *   3. Loop-guard via sessionStorage: if this is the first attempt, do the
 *      nuclear reset (unregister SWs + clear caches + reload). After the
 *      reload, /ping fires again — if we're STILL too old, automation has
 *      failed, route to /update-required for manual instructions.
 *   4. If we ARE up to spec (or no cutoff is set), clear the loop-guard
 *      flag so the next mismatch (if it ever happens) starts fresh.
 *
 * Failures (network, parse, 5xx) are non-fatal: log and skip. We don't
 * force a reset for transient errors — the user can keep working with
 * whatever version they have.
 */
export async function runVersionCheck(): Promise<void> {
  let ping;
  try {
    ping = await pingApi();
  } catch (err) {
    logError(
      err instanceof Error ? err : new Error(String(err)),
      'versionCheck: /ping failed, skipping',
    );
    return;
  }

  const minVersion = ping.appVersionRequired;
  const ourVersion = import.meta.env.VITE_VERSION as string | undefined;

  logDebug('versionCheck: /ping returned', undefined, {
    appVersionRequired: minVersion,
    ourVersion,
  });

  if (!minVersion) {
    // No hard cutoff configured. Clear any stale loop-guard from a
    // previous cutoff that's now been relaxed.
    sessionStorage.removeItem(FORCE_UPDATE_FLAG);
    return;
  }

  if (!ourVersion) {
    logDebug('versionCheck: VITE_VERSION missing, cannot compare — skipping');
    return;
  }

  const cmp = compareSemver(ourVersion, minVersion);
  if (cmp === null) {
    logDebug('versionCheck: unparseable version string(s), skipping');
    return;
  }

  if (cmp >= 0) {
    // We meet or exceed the cutoff. Clear loop-guard so future mismatches
    // start with a fresh automatic attempt.
    sessionStorage.removeItem(FORCE_UPDATE_FLAG);
    logDebug('versionCheck: version OK', undefined, { ourVersion, minVersion });
    return;
  }

  // We're behind the cutoff.
  const alreadyAttempted = sessionStorage.getItem(FORCE_UPDATE_FLAG) === '1';
  if (alreadyAttempted) {
    // Loop-guard fired: we already did the nuclear reset once this session
    // and we're still too old. Automation can't fix this — surface the
    // manual recovery page.
    logDebug('versionCheck: still behind after nuclear reset, routing to UpdateRequired', undefined, {
      ourVersion,
      minVersion,
    });
    if (window.location.pathname !== UPDATE_REQUIRED_ROUTE) {
      window.location.replace(UPDATE_REQUIRED_ROUTE);
    }
    return;
  }

  // First attempt: mark the flag, then nuke and reload.
  sessionStorage.setItem(FORCE_UPDATE_FLAG, '1');
  logDebug('versionCheck: behind minimum version, triggering nuclear reset', undefined, {
    ourVersion,
    minVersion,
  });
  await nuclearReset();
  // nuclearReset never resolves — the page is reloading.
}
